import { TABLE_TYPES, tableTypeText } from '../constants/tables';
import moment from '../moment/moment';
import { getCSSvar } from './utils';

// export const getTables = tables => tables.map( table => {return {...table, timelineGroupId: table.id, timelineGroupName: "Table " + table.name}});
export const getTables = (tables, t) =>
    tables.map(table => ({
        ...table,
        timelineGroupId: table.id,
        timelineGroupName: t('booker:table_name', { name: table.name }),
    }));

export const getTablesByZone = (tables, zoneId) => {
    if (zoneId <= 0)
        return tables;
    return tables.filter(t => t.zone.id === zoneId);
};

export const getEndTimeMoment = (reservation) => {
    const [hours, minutes, seconds] = reservation.duration.split(":");
    const from_datetime = moment(reservation.start_time);
    const to_datetime = moment(reservation.start_time)
        .add(parseInt(hours), "hours")
        .add(parseInt(minutes), "minutes");
    return {
        from_datetime: from_datetime,
        to_datetime: to_datetime
    }
};

const initColors = { taken: 'red', hasOverlap: 'yellow', inactive: 'grey', available: 'green' };

const getPlanAvailabilityColors = () => ({
    hasOverlap: getCSSvar('--table-overlap'),
    available: getCSSvar('--table-available'),
    taken: getCSSvar('--table-taken'),
    arrived: getCSSvar('--table-arrived'),
    noShow: getCSSvar('--table-noShow'),
    inactive: "#808080",
});

export const getAvailabilityColor = (table, maxOverlapAmount = 900, colors = initColors) => {
    colors = { ...initColors, ...colors };
    table.color = null;
    if (table.overlap)
        table.color = table.overlapAmount <= maxOverlapAmount ? colors.hasOverlap : colors.taken;
    else
        table.color = !table.active ? colors.inactive : colors.available;
};

export function checkFloorPlanAvailability(
    durationCallback, 
    tableData, 
    reservationList = [], 
    currentMoment = moment(),
) {
    const colors = getPlanAvailabilityColors();

    const resultAvailability = {
        color: colors.available,
        nextReservations: [],
    }

    const status = {
        taken: false,
        hasOverlap: false,
        inactive: !tableData.active,
        noShows: 0,
        arrived: false,
    };

    let reservations_on_table = 0;

    for (const r of reservationList) {

        if (r.cancelled || r.hidden || !r.tables.includes(tableData.id)) {
            continue;
        }

        const guests = r.adults + r.children;
        // const duration = moment.duration(durationCallback(guests, r.start_time));
        const duration = moment.duration(r.duration); // TODO: revert this when a true fix is provided

        const { from_datetime, to_datetime } = getEndTimeMoment(r);
        const overlap_datetime = moment(from_datetime).subtract(duration);
        if (currentMoment.isAfter(overlap_datetime) && currentMoment.isBefore(from_datetime)) {
            status.hasOverlap = true;
            if (!r.no_show)
                resultAvailability.nextReservations.push(r);
        }
        if (currentMoment.isSameOrAfter(from_datetime) && currentMoment.isBefore(to_datetime)) {
            status.taken = true;
            if (!r.no_show)
                resultAvailability.nextReservations.push(r);
            if (r.no_show)
                status.noShows++;
            reservations_on_table++;
        }

        if (r.arrived && currentMoment.isSameOrAfter(overlap_datetime) && currentMoment.isBefore(to_datetime)) {
            status.arrived = true;
        }
    }

    if (status.inactive)
        resultAvailability.color = colors.inactive;

    if (status.hasOverlap)
        resultAvailability.color = colors.hasOverlap;

    if (status.taken)
        resultAvailability.color = colors.taken;

    if (status.arrived)
        resultAvailability.color = colors.arrived;

    if (status.noShows >= reservations_on_table && status.noShows > 0)
        resultAvailability.color = status.hasOverlap ? colors.hasOverlap : colors.available;

    return resultAvailability;
}

/**
 * Returns tables with overlap data.
 * @param {string} duration 
 * @param {Array} tableList 
 * @param {Array} reservationList 
 * @param {number} zoneId 
 * @param {import('moment').Moment} currentMoment 
 * @returns {Array}
 */
export function checkTableAvailable(
    duration, 
    tableList = [], 
    reservationList = [], 
    zoneId = 1, 
    currentMoment = moment()
) {
    
    if (zoneId > 0) {
        tableList = tableList.filter(t => t.zone.id === zoneId);
    }
    
    const tables = JSON.parse(JSON.stringify(tableList));

    const durationMoment = moment.duration(duration)
    const currentReservationEnd = moment(currentMoment).add(durationMoment)

    const overlapReservations = reservationList
        .filter(reservation => {

            if (!reservation.confirmed || reservation.cancelled)
                return false;

            const { from_datetime, to_datetime } = getEndTimeMoment(reservation);
            if (from_datetime.date() === currentMoment.date() || to_datetime.date() === currentMoment.date()) {
                return !(currentMoment >= to_datetime || currentReservationEnd <= from_datetime);
            }

            return false;
        });

    overlapReservations.forEach(reservation => {
        const { from_datetime, to_datetime } = getEndTimeMoment(reservation);
        let overlapAmount = 0;
        if (currentMoment < from_datetime) {
            overlapAmount = currentReservationEnd.diff(from_datetime, "seconds");
        }
        else {
            overlapAmount = to_datetime.diff(currentMoment, "seconds")
        }
        reservation.tables.forEach(table => {
            const tableIdx = tables.findIndex(obj => obj.id === table);
            if (tableIdx !== -1) {
                if (tables[tableIdx].overlap) {
                    tables[tableIdx].overlapAmount += overlapAmount;
                    tables[tableIdx].overlapReservations = tables[tableIdx].overlapReservations.concat([reservation]);
                }
                else {
                    tables[tableIdx].overlapAmount = overlapAmount;
                    tables[tableIdx].overlapReservations = [reservation];
                }
                tables[tableIdx].overlap = true;
            }
        });
    });

    return tables;
};

export const tableTypesToOptions = (types = []) => types.sort((a, b) => b === 'OVAL' ? -1 : 1).map((type, key) => {
    return {
        key,
        text: tableTypeText[type],
        value: type,
    };
});

export const getInitTableForm = () => {
    return {
        id: { value: null, modified: false, type: "id", valid: true },
        name: { value: "", modified: false, type: "not_empty_string", valid: false },
        seats: { value: 2, modified: false, type: "table_seats", valid: true },
        active: { value: true, modified: false, type: "table_active", valid: true },
        terrace: { value: false, modified: false, type: "table_terrace", valid: true },
        high: { value: false, modified: false, type: "table_high", valid: true },
        min_seats: { value: 0, modified: false, type: "positive_number", valid: true },
        zone: { value: null, modified: false, type: "table_zone", valid: true },
        type: { value: TABLE_TYPES.RECTANGLE, modified: false, type: "table_type", valid: true },
    }
};

export const tableToForm = (table, zone = null) => {
    const newForm = getInitTableForm();
    newForm.id.value = table.id;
    newForm.name.value = table.name;
    newForm.seats.value = table.seats;
    newForm.active.value = table.active;
    newForm.high.value = table.high;
    newForm.terrace.value = table.terrace;
    newForm.zone.value = zone ?? table.zone.id;
    newForm.type.value = table.type;
    newForm.min_seats.value = table.min_seats;
    return newForm;
};

export const formToTable = (form) => {
    return {
        id: form.id.value,
        name: form.name.value,
        seats: form.seats.value,
        active: form.active.value,
        high: form.high.value,
        terrace: form.terrace.value,
        zone_id: form.zone.value,
        min_seats: form.min_seats.value,
        type: form.type.value,
    };
};

export const getInitTableLinkForm = () => {
    return {
        id: { value: null, modified: false, type: "id", valid: true },
        tables: { value: [], modified: false, type: "tables", valid: false },
        min_seats: { value: 0, modified: false, type: "positive_number", valid: true },
        max_seats: { value: 0, modified: false, type: "positive_number", valid: true },
    }
};

export const formToTableLink = (form) => {
    return {
        id: form.id.value,
        tables: form.tables.value,
        min_seats: form.min_seats.value,
        max_seats: form.max_seats.value,
    };
};

export const tableLinkToForm = (tableLink) => {
    const newForm = getInitTableLinkForm();
    newForm.id.value = tableLink.id;
    newForm.tables.value = tableLink.tables.map(table => table.id);
    newForm.max_seats.value = tableLink.max_seats;
    newForm.min_seats.value = tableLink.min_seats;
    return newForm
};



// WEBPACK FOOTER //
// ./src/helpers/tables.js